import { captureSentryException } from '@apexx/common-composables';
import { ref, watch, type Ref } from 'vue';

export type LaunchFailureRecoveryOptions = {
	appId: Ref<string>;
	appVersion: Ref<string | undefined>;
	onRecoverToWelcome: () => void;
};

export type LaunchFailureOutcome = 'show_error' | 'recover_to_welcome';

/**
 * Tracks consecutive hub mount failures for the current appId, reports each to
 * Sentry, and decides whether to show the error UI or bounce to welcome.
 */
export function useLaunchFailureRecovery({
	appId,
	appVersion,
	onRecoverToWelcome,
}: LaunchFailureRecoveryOptions): {
	launchError: Ref<string | undefined>;
	recordSuccess: () => void;
	recordFailure: (error: unknown) => LaunchFailureOutcome;
	refreshLaunch: (retry: () => void) => void;
	recoverToWelcome: () => void;
} {
	const launchError = ref<string>();
	const consecutiveFailures = ref(0);

	watch(appId, () => {
		consecutiveFailures.value = 0;
		launchError.value = undefined;
	});

	function formatLaunchError(error: unknown): string {
		if (error instanceof Error && error.message) {
			return error.message;
		}

		if (typeof error === 'string' && error.length > 0) {
			return error;
		}

		try {
			const serialized = JSON.stringify(error);
			if (serialized && serialized !== '{}') {
				return serialized;
			}
		} catch {
			// Fall through to generic message.
		}

		console.error('[hub] Failed to launch app', appId.value, error);

		return 'Failed to launch app';
	}

	function recordSuccess(): void {
		consecutiveFailures.value = 0;
		launchError.value = undefined;
	}

	function recordFailure(error: unknown): LaunchFailureOutcome {
		consecutiveFailures.value += 1;
		const attempt = consecutiveFailures.value;

		captureSentryException(error, {
			tags: {
				hub_launch: 'mount_failed',
				app_id: appId.value,
			},
			extra: {
				appId: appId.value,
				attempt,
				userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
				appVersion: appVersion.value,
			},
		});

		if (attempt >= 2) {
			launchError.value = undefined;
			onRecoverToWelcome();
			consecutiveFailures.value = 0;
			return 'recover_to_welcome';
		}

		launchError.value = formatLaunchError(error);
		return 'show_error';
	}

	function refreshLaunch(retry: () => void): void {
		launchError.value = undefined;
		retry();
	}

	function recoverToWelcome(): void {
		launchError.value = undefined;
		consecutiveFailures.value = 0;
		onRecoverToWelcome();
	}

	return {
		launchError,
		recordSuccess,
		recordFailure,
		refreshLaunch,
		recoverToWelcome,
	};
}
